import { Fragment, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  CELESTIAL_CHORDS,
  CHORD_COLORS,
  CHORD_LABELS,
  previewCelestialChord,
  type ChordId,
} from '../audio/celestialPadEngine';
import { isChordRunHold, isChordRunStart } from '../utils/chordAccompaniment';
import {
  hasCustomVoicing,
  resolveVoicing,
  resetChordVoicing,
  setChordVoicing,
} from '../utils/chordVoicing';
import { ChordVoicingEditor } from './ChordVoicingEditor';
import { DRUM_STEPS_PER_MEASURE } from '../types';
import type { ChordAccompaniment, MeasureChordGrid } from '../types';

interface Props {
  grid: MeasureChordGrid;
  accompaniment: ChordAccompaniment;
  bpm: number;
  activeStep: number | null;
  onAccompanimentChange: (next: ChordAccompaniment) => void;
  onToggleStep: (chordId: ChordId, stepIndex: number) => void;
  onExtendRange: (chordId: ChordId, fromStep: number, toStep: number) => void;
  onClearRow: (chordId: ChordId) => void;
}

export function ChordStepGrid({
  grid,
  accompaniment,
  bpm,
  activeStep,
  onAccompanimentChange,
  onToggleStep,
  onExtendRange,
  onClearRow,
}: Props) {
  const totalSteps = DRUM_STEPS_PER_MEASURE;
  const stepSec = 60 / bpm / 4;
  const anchorRef = useRef<{ chordId: ChordId; step: number } | null>(null);
  const [editingChordId, setEditingChordId] = useState<ChordId | null>(null);

  const previewHold = (chordId: ChordId, steps: number) => {
    const freqs = resolveVoicing(accompaniment, chordId);
    previewCelestialChord(chordId, Math.max(stepSec, steps * stepSec * 0.98), freqs);
  };

  const handleCellClick = (chordId: ChordId, step: number, shiftKey: boolean) => {
    if (shiftKey && anchorRef.current?.chordId === chordId) {
      const from = anchorRef.current.step;
      const lo = Math.min(from, step);
      const hi = Math.max(from, step);
      onExtendRange(chordId, from, step);
      previewHold(chordId, hi - lo + 1);
      anchorRef.current = { chordId, step: lo };
      return;
    }

    const wasOn = grid.steps[chordId][step];
    onToggleStep(chordId, step);
    anchorRef.current = { chordId, step };

    if (!wasOn) {
      previewHold(chordId, 1);
    }
  };

  return (
    <div className="chord-step-grid drum-step-grid" role="grid" aria-label="Acordes del compás">
      <div className="drum-step-grid__ruler" aria-hidden="true">
        <span className="drum-step-grid__corner" />
        <div className="drum-step-grid__ruler-track">
          {Array.from({ length: totalSteps }, (_, step) => {
            const beatStart = step % 4 === 0;
            const beatInBar = Math.floor(step / 4) + 1;
            return (
              <span
                key={step}
                className={[
                  'drum-step-grid__ruler-cell',
                  beatStart ? 'drum-step-grid__ruler-cell--beat' : '',
                  step === 0 ? 'drum-step-grid__ruler-cell--bar' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {step === 0 ? 'C' : beatStart ? beatInBar : ''}
              </span>
            );
          })}
        </div>
      </div>

      {CELESTIAL_CHORDS.map((chord) => {
        const custom = hasCustomVoicing(accompaniment, chord.id);
        const notes = resolveVoicing(accompaniment, chord.id);

        return (
          <Fragment key={chord.id}>
            <div className="drum-step-grid__row" role="row">
              <div className="drum-step-grid__track" role="rowheader">
                <button
                  type="button"
                  className="drum-step-grid__track-dot-btn"
                  style={{ '--drum-color': CHORD_COLORS[chord.id] } as CSSProperties}
                  onClick={() => previewHold(chord.id, 1)}
                  title={`Escuchar ${CHORD_LABELS[chord.id]}`}
                  aria-label={`Escuchar ${CHORD_LABELS[chord.id]}`}
                >
                  <span className="drum-step-grid__track-dot" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={`drum-step-grid__track-name ${editingChordId === chord.id ? 'drum-step-grid__track-name--open' : ''} ${custom ? 'drum-step-grid__track-name--custom' : ''}`}
                  onClick={() =>
                    setEditingChordId((prev) => (prev === chord.id ? null : chord.id))
                  }
                  title="Editar notas del acorde"
                >
                  {chord.label}
                  {custom ? <span className="drum-step-grid__track-badge">✎</span> : null}
                </button>
                <button
                  type="button"
                  className="drum-step-grid__clear"
                  onClick={() => onClearRow(chord.id)}
                  title={`Limpiar ${chord.label}`}
                >
                  ×
                </button>
              </div>

              <div className="drum-step-grid__steps">
                {Array.from({ length: totalSteps }, (_, step) => {
                  const row = grid.steps[chord.id];
                  const on = row[step];
                  const runStart = isChordRunStart(row, step);
                  const runHold = isChordRunHold(row, step);
                  const beatStart = step % 4 === 0;
                  const isActive = activeStep === step;

                  return (
                    <button
                      key={step}
                      type="button"
                      role="gridcell"
                      className={[
                        'drum-step-grid__cell',
                        on ? 'drum-step-grid__cell--on' : '',
                        runStart ? 'chord-step-grid__cell--start' : '',
                        runHold ? 'chord-step-grid__cell--hold' : '',
                        beatStart ? 'drum-step-grid__cell--beat' : '',
                        step === 0 ? 'drum-step-grid__cell--bar' : '',
                        isActive ? 'drum-step-grid__cell--active' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ '--drum-color': CHORD_COLORS[chord.id] } as CSSProperties}
                      aria-pressed={on}
                      aria-label={`${CHORD_LABELS[chord.id]}, paso ${step + 1}`}
                      onClick={(e) => handleCellClick(chord.id, step, e.shiftKey)}
                    />
                  );
                })}
              </div>
            </div>

            {editingChordId === chord.id && (
              <div className="chord-voicing-editor-wrap">
                <ChordVoicingEditor
                  chordId={chord.id}
                  notes={notes}
                  isCustom={custom}
                  onChange={(next) =>
                    onAccompanimentChange(setChordVoicing(accompaniment, chord.id, next))
                  }
                  onReset={() =>
                    onAccompanimentChange(resetChordVoicing(accompaniment, chord.id))
                  }
                  onClose={() => setEditingChordId(null)}
                />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
